import './SCPolicies.scss';
import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import LoadingScreen from '../LoadingScreen';
import NoAccessScreen from '../NoAccessScreen';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Link } from '@lmig/lmds-react-link';
// import { toTitleCase } from 'Utilities/StringFormatter';
import { FilterCard } from '../FilterCard';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { useUserData } from 'Foundation/User/UserContext';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { useSessionStorage } from 'Utilities/UseStorage';
import { getPolicyURLByHref } from 'Utilities/URLHelper';
import { getRecentActivitiesLink } from 'Foundation/Configurations/GlobalConfiguration';
// import { OverflowTooltip } from 'Foundation/Common/Tooltip';

enum TransactionType {
  Audit = 'Audit',
  Cancellation = 'Cancellation',
  Endorsement = 'Endorsement',
  NewBusiness = 'New business',
  Prerenewal = 'Pre-renewal',
  Renewal = 'Renewal',
}

interface IPolicyDocument {
  AgencyName: string;
  AgencyCode: string;
  PolicyNumber: string;
  InsuredName: string;
  ActivityDate: string;
  TransactionType: string;
  Pages: string;
  PolicyHref: string;
}

enum SortDirection {
  ascending = 'ascending',
  descending = 'descending',
  none = 'none',
}

enum SCPolicyTableSortableKeys {
  policy = 'policy',
  insuredName = 'insuredName',
  activity = 'activity',
}

// Map table keys to actual property names in IPolicyDocument
const keyToProp: Record<SCPolicyTableSortableKeys, keyof IPolicyDocument> = {
  [SCPolicyTableSortableKeys.policy]: 'PolicyNumber',
  [SCPolicyTableSortableKeys.insuredName]: 'InsuredName',
  [SCPolicyTableSortableKeys.activity]: 'TransactionType',
};

interface SortDirections {
  [SCPolicyTableSortableKeys.policy]: SortDirection;
  [SCPolicyTableSortableKeys.insuredName]: SortDirection;
  [SCPolicyTableSortableKeys.activity]: SortDirection;
}

const GetEmptySortDirections = {
  [SCPolicyTableSortableKeys.policy]: SortDirection.none,
  [SCPolicyTableSortableKeys.insuredName]: SortDirection.none,
  [SCPolicyTableSortableKeys.activity]: SortDirection.none,
};

export default function Policies(props: { activeTab: number }) {
  const { userData } = useUserData();
  const resultsPerPage = 5;
  const maxResults = 49;
  const expirationMinutes = 12 * 60;
  const [page, setPage] = useState(0);
  const [policies, setPolicies] = useSessionStorage<{
    activities?: IPolicyDocument[] | null;
    isLoaded: boolean;
    noAccess: boolean;
  }>(
    'sc-policies-session:' + userData?.selectedStatCodeData?.SubStat,
    { isLoaded: false, noAccess: false },
    expirationMinutes,
  );
  const [filteredPolicies, setFilteredPolicies] = useState<IPolicyDocument[]>([]);
  const [filtersSubjectCol, setSubjectColFilters] = useState<Array<TransactionType>>([]);
  const [currentSortKey, setCurrentSortKey] = useState<SCPolicyTableSortableKeys>(
    SCPolicyTableSortableKeys.insuredName,
  );
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    ...GetEmptySortDirections,
    [SCPolicyTableSortableKeys.insuredName]: SortDirection.descending,
  });
  const [counts, setCounts] = useState<Array<[TransactionType, number]>>([
    [TransactionType.Audit, 0],
    [TransactionType.Cancellation, 0],
    [TransactionType.Endorsement, 0],
    [TransactionType.NewBusiness, 0],
    [TransactionType.Prerenewal, 0],
    [TransactionType.Renewal, 0],
  ]);

  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });

  const LMDSPagination = () => {
    const itemCount = filteredPolicies?.length;
    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    return (
      (itemCount > resultsPerPage && (
        <Pagination
          id="policies-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={resultsPerPage}
        />
      )) || <></>
    );
  };

  const policyTableSorter = (key: SCPolicyTableSortableKeys) => {
    setSortDirections((prev) => ({
      ...GetEmptySortDirections,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
  };

  const toggleSubjectFilter: any = (value: TransactionType) => {
    if (filtersSubjectCol.includes(value))
      setSubjectColFilters(filtersSubjectCol.filter((filterVal: any) => filterVal !== value));
    else setSubjectColFilters([...filtersSubjectCol, value]);
  };

  const sortTable = (unsorted: IPolicyDocument[]) => {
    const sorted = unsorted.sort((a: IPolicyDocument, b: IPolicyDocument) => {
      const key = keyToProp[currentSortKey];
      if (sortDirections[currentSortKey] === SortDirection.ascending)
        return a[key].toLowerCase().localeCompare(b[key].toLowerCase());
      else return b[key].toLowerCase().localeCompare(a[key].toLowerCase());
    });
    return sorted.slice(0, maxResults);
  };

  const areMoreRecordsThanAvailable = () => {
    return policies?.activities && filteredPolicies.length < policies.activities.length && page === 9;
  };

  useEffect(() => {
    const fetchData = async () => {
      setPolicies({ data: { activities: null, isLoaded: false, noAccess: false } });
      let noAccessResponse = false;
      let response = (await SitecoreFetch(
        `/api/sitecore/PolicyAsync/GetFilteredPolicyRecentActivities`,
      )) as IServerResponse;
      let result: IPolicyDocument[] | null = null;
      if (response.Status.Code === HttpStatusCodes.OK) result = response.Data as IPolicyDocument[];
      else if (response.Status.Code === HttpStatusCodes.Forbidden) {
        noAccessResponse = true;
      }
      setPolicies({ data: { activities: result, isLoaded: true, noAccess: noAccessResponse } });
    };

    try {
      if (userData?.isAgent() || userData?.isMockingAgent) {
        if (!policies?.isLoaded) fetchData();
      } else {
        setPolicies({ data: { activities: null, isLoaded: true, noAccess: false } });
      }
    } catch (error) {
      console.error(error);
    }
  }, [userData?.selectedStatCodeData?.SubStat]);

  useEffect(() => {
    const getTransactionTypeCount = (t: TransactionType) => {
      return policies?.activities ? policies.activities.filter((p) => p.TransactionType == t).length : 0;
    };

    if (policies?.isLoaded) {
      if (policies.activities && policies.activities?.length > 0) {
        setCounts([
          [TransactionType.Audit, getTransactionTypeCount(TransactionType.Audit)],
          [TransactionType.Cancellation, getTransactionTypeCount(TransactionType.Cancellation)],
          [TransactionType.Endorsement, getTransactionTypeCount(TransactionType.Endorsement)],
          [TransactionType.NewBusiness, getTransactionTypeCount(TransactionType.NewBusiness)],
          [TransactionType.Prerenewal, getTransactionTypeCount(TransactionType.Prerenewal)],
          [TransactionType.Renewal, getTransactionTypeCount(TransactionType.Renewal)],
        ]);
        setFilteredPolicies(sortTable(policies.activities));
      } else {
        GetNoDataTile('policies', setNoDataTile);
      }
    }
  }, [policies?.isLoaded, policies?.activities]);

  useEffect(() => {
    if (policies?.activities) {
      var filtered: IPolicyDocument[] = policies.activities;
      if (filtersSubjectCol.length > 0) {
        filtered = policies.activities.filter((a) => filtersSubjectCol.includes(a.TransactionType as TransactionType));
      }
      setFilteredPolicies(sortTable(filtered));
      setPage(0);
    }
  }, [filtersSubjectCol, sortDirections]);

  return !policies?.isLoaded ? (
    <LoadingScreen />
  ) : policies?.noAccess ? (
    <NoAccessScreen tabName="policy" />
  ) : policies?.activities && policies.activities.length !== 0 && userData ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="policies-tile"
      children={[
        <>
          <div id="sc-policies-table-container" className="recent-activities-table-container">
            <div key={props.activeTab} className="sc-policies-tab-content tab-content">
              <div className="sc-policy-transactions-graphic">
                <div className="filterCard">
                  <FilterCard activeFilters={filtersSubjectCol} list={counts} filterFunction={toggleSubjectFilter} />
                </div>
              </div>
              <div className="design-system-data-table-container">
                {filteredPolicies.length !== 0 ? (
                  <div className="design-system-data-table">
                    <DataTable>
                      <Table className="design-system-table" spacing="micro">
                        <TableHeader id="sc-policies-table-header">
                          <TableRow>
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.policy}
                              onClick={() => policyTableSorter(SCPolicyTableSortableKeys.policy)}
                              className="policy-number-header"
                            >
                              Policy
                            </TableCell>
                            {/* <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.insuredName}
                              onClick={() => policyTableSorter(SCPolicyTableSortableKeys.insuredName)}
                              className="policy-insured-name-header"
                            >
                              Insured name
                            </TableCell> */}
                            <TableCell
                              type="colHead"
                              isSortable
                              sortDirection={sortDirections.activity}
                              onClick={() => policyTableSorter(SCPolicyTableSortableKeys.activity)}
                              className="policy-activity-type-header"
                            >
                              Activity
                            </TableCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody id="sc-policies-table-body">
                          {filteredPolicies.length ? (
                            filteredPolicies
                              .slice(
                                page * resultsPerPage,
                                Math.min(page * resultsPerPage + resultsPerPage, filteredPolicies?.length),
                              )
                              .map((data) => (
                                <TableRow>
                                  <TableCell className="policy-number-cell">
                                    <Link target="_blank" href={getPolicyURLByHref(data.PolicyHref, userData)}>
                                      {data.PolicyNumber}
                                    </Link>
                                  </TableCell>
                                  {/* <TableCell className="policy-insured-name-cell">
                                    <OverflowTooltip text={toTitleCase(data.InsuredName) ?? ''}>
                                      <div>{toTitleCase(data.InsuredName) ?? ''}</div>
                                    </OverflowTooltip>
                                  </TableCell> */}
                                  <TableCell className="policy-activity-type-cell">
                                    <div>{data.TransactionType}</div>
                                  </TableCell>
                                </TableRow>
                              ))
                          ) : (
                            <></>
                          )}
                        </TableBody>
                      </Table>
                    </DataTable>

                    {areMoreRecordsThanAvailable() && (
                      <div className="view-all-recent-activities">
                        <Link
                          target="_blank"
                          href={getRecentActivitiesLink()}
                          variant="standalone"
                          className="portal-tile-v2-header-link"
                        >
                          View all recent activities
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-data-found">
                    <div className="no-data-found-inner"></div>
                    No policies found. Try another filter
                  </div>
                )}
              </div>
            </div>
            <LMDSPagination />
          </div>
        </>,
      ]}
    ></FlexibleContentTile>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile?.leftText}
      messageRight={noDataTile?.rightText}
    ></RecentActivitiesNoData>
  );
}
